// Centralized configuration for environment variables
// All environment variable access should go through this module

interface Config {
  // API Configuration
  api: {
    baseUrl: string;
  };

  // Authentication (Privy)
  privy: {
    appId: string;
    clientId: string;
  };

  // Error Tracking (Sentry)
  sentry: {
    dsn: string | undefined;
    enabled: boolean;
  };

  // Application
  app: {
    version: string;
    environment: string;
    isDevelopment: boolean;
    isProduction: boolean;
  };
}

// Environment variable validation
function validateEnvVar(name: string, value: string | undefined): string {
  if (!value || value.trim() === '') {
    throw new Error(
      `Environment variable ${name} is required but not provided`
    );
  }
  return value;
}

// Create configuration object with validation
const config: Config = {
  api: {
    baseUrl: import.meta.env.VITE_API_BASE_URL || 'https://localhost',
  },

  privy: {
    appId: validateEnvVar(
      'VITE_PRIVY_APP_ID',
      import.meta.env.VITE_PRIVY_APP_ID
    ),
    clientId: validateEnvVar(
      'VITE_PRIVY_CLIENT_ID',
      import.meta.env.VITE_PRIVY_CLIENT_ID
    ),
  },

  sentry: {
    dsn: import.meta.env.VITE_SENTRY_DSN,
    enabled: !!import.meta.env.VITE_SENTRY_DSN,
  },

  app: {
    version: import.meta.env.VITE_APP_VERSION || 'unknown',
    environment: import.meta.env.MODE || 'development',
    isDevelopment: import.meta.env.DEV,
    isProduction: import.meta.env.PROD,
  },
};

// Validate critical environment variables at startup
try {
  // Only validate in non-test environments
  if (config.app.environment !== 'test') {
    // Privy configuration is critical for auth
    if (!config.privy.appId) throw new Error('Missing Privy App ID');
    if (!config.privy.clientId) throw new Error('Missing Privy Client ID');
  }
} catch (error) {
  console.error('Configuration validation failed:', error);
  throw error;
}

// Export the configuration
export { config };

// Export individual sections for convenience
export const { api, privy, sentry, app } = config;

// Export for use in other modules
export default config;
